import { useEffect, useRef, useState } from "react";
import { Plus, Loader2, Trash2, ScanLine, Upload, FileText, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MODALITIES = ["USG Pelvis", "TVS (Transvaginal)", "Follicular Study", "NT / NB Scan", "Anomaly Scan (TIFFA)", "Growth Scan", "Doppler Study", "Obstetric USG", "Mammography", "X-Ray", "CT", "MRI", "Other"];
const EMPTY_ORDER = { modality: "USG Pelvis", study: "", ordered_date: new Date().toISOString().slice(0, 10), priority: "Routine", notes: "" };

export default function Radiology({ patientId }) {
    const [orders, setOrders] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [form, setForm] = useState(EMPTY_ORDER);
    const [reporting, setReporting] = useState(null);
    const [findings, setFindings] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileRef = useRef(null);
    const uploadTarget = useRef(null);

    const openPreview = async (o) => {
        try {
            const res = await api.get(`/radiology/${o.id}/dicom-preview`, { responseType: "blob" });
            setPreview({ url: window.URL.createObjectURL(new Blob([res.data], { type: "image/png" })), order: o });
        } catch { toast.error("No image preview available for this DICOM (metadata-only file)"); }
    };

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/radiology`);
            setOrders(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { load(); }, [patientId]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/radiology`, form);
            toast.success(`${form.modality} ordered`);
            setShowNew(false);
            setForm(EMPTY_ORDER);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const markReported = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/radiology/${reporting.id}`, { status: "Reported", findings, reported_date: new Date().toISOString().slice(0, 10) });
            toast.success("Marked as reported");
            setReporting(null);
            setFindings("");
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        const target = uploadTarget.current;
        if (!f || !target) return;
        setUploading(target.id);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/radiology/${target.id}/upload`, fd);
            toast.success("Report file attached");
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setUploading(null); e.target.value = ""; }
    };

    const viewFile = async (o) => {
        try {
            const res = await api.get(`/radiology/${o.id}/file`, { responseType: "blob" });
            window.open(window.URL.createObjectURL(new Blob([res.data], { type: o.content_type || "application/pdf" })), "_blank");
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const remove = async (o) => {
        if (!window.confirm(`Delete ${o.modality} order?`)) return;
        try {
            await api.delete(`/radiology/${o.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    return (
        <div className="space-y-4" data-testid="radiology-section">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ScanLine className="h-4 w-4 text-[#E06D1F]" /> Radiology / Imaging</h3>
                <Button data-testid="new-radiology-btn" size="sm" onClick={() => setShowNew(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> Order Scan</Button>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.dcm" className="hidden" data-testid="radiology-file-input" onChange={onUpload} />

            {!orders ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#E06D1F]" /></div>
            ) : orders.length === 0 ? (
                <p className="text-sm text-slate-500 py-4" data-testid="no-radiology">No imaging orders yet.</p>
            ) : (
                <div className="space-y-3">
                    {orders.map((o) => (
                        <div key={o.id} data-testid={`radiology-card-${o.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                        {o.modality}{o.study ? ` — ${o.study}` : ""}
                                        <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${o.status === "Reported" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : o.status === "Cancelled" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`radiology-status-${o.id}`}>{o.status}</span>
                                        {o.priority === "Urgent" && <span className="ml-1.5 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600">URGENT</span>}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">Ordered: {o.ordered_date}{o.reported_date ? ` · Reported: ${o.reported_date}` : ""}</p>
                                    {o.findings && <p className="text-xs text-slate-700 mt-1.5 whitespace-pre-line"><b>Findings:</b> {o.findings}</p>}
                                    {o.content_type === "application/dicom" && (
                                        <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/50 px-3 py-2" data-testid={`dicom-info-${o.id}`}>
                                            <p className="text-[11px] font-bold text-indigo-700">DICOM{o.dicom_meta?.modality ? ` · ${o.dicom_meta.modality}` : ""}{o.dicom_meta?.study_date ? ` · ${o.dicom_meta.study_date}` : ""}</p>
                                            <p className="text-xs text-slate-600">{[o.dicom_meta?.study_description, o.dicom_meta?.institution, o.dicom_meta?.patient_name && `File patient: ${o.dicom_meta.patient_name}`].filter(Boolean).join(" · ") || "DICOM file attached"}</p>
                                        </div>
                                    )}
                                    {o.notes && <p className="text-xs text-slate-500 mt-0.5">Notes: {o.notes}</p>}
                                </div>
                                <div className="flex gap-1.5">
                                    {o.status === "Ordered" && (
                                        <Button data-testid={`radiology-report-btn-${o.id}`} size="sm" variant="outline" onClick={() => { setReporting(o); setFindings(o.findings || ""); }} className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CheckCircle className="h-3.5 w-3.5" /> Enter Report</Button>
                                    )}
                                    {o.dicom_preview_path && (
                                        <Button data-testid={`dicom-preview-${o.id}`} size="sm" variant="ghost" onClick={() => openPreview(o)} className="text-indigo-600 hover:text-indigo-700 gap-1"><ScanLine className="h-4 w-4" /> Preview</Button>
                                    )}
                                    {o.file_path ? (
                                        <Button data-testid={`radiology-file-${o.id}`} size="sm" variant="ghost" onClick={() => viewFile(o)} className="text-slate-500 hover:text-[#E06D1F] gap-1"><FileText className="h-4 w-4" /> File</Button>
                                    ) : (
                                        <Button data-testid={`radiology-upload-${o.id}`} size="sm" variant="ghost" onClick={() => { uploadTarget.current = o; fileRef.current?.click(); }} disabled={uploading === o.id} className="text-slate-500 hover:text-[#E06D1F] gap-1">
                                            {uploading === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Attach
                                        </Button>
                                    )}
                                    <Button data-testid={`radiology-delete-${o.id}`} size="sm" variant="ghost" onClick={() => remove(o)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="max-w-lg" data-testid="radiology-order-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Order Imaging</DialogTitle>
                        <DialogDescription>Radiology order tracked until the report is entered.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Modality *</Label>
                                <select data-testid="radiology-modality-select" value={form.modality} onChange={set("modality")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                    {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5"><Label>Order Date</Label><Input data-testid="radiology-date-input" type="date" value={form.ordered_date} onChange={set("ordered_date")} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Study / Clinical Details</Label><Input data-testid="radiology-study-input" value={form.study} onChange={set("study")} placeholder="e.g. Rule out fibroid, 12w viability scan" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <select data-testid="radiology-priority-select" value={form.priority} onChange={set("priority")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                    <option value="Routine">Routine</option>
                                    <option value="Urgent">Urgent</option>
                                </select>
                            </div>
                            <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="radiology-notes-input" value={form.notes} onChange={set("notes")} placeholder="Optional" /></div>
                        </div>
                        <Button data-testid="radiology-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Order Scan</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
                <DialogContent className="max-w-2xl" data-testid="dicom-preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">DICOM Preview — {preview?.order?.modality}</DialogTitle>
                        <DialogDescription>{preview?.order?.dicom_meta?.study_description || "Rendered from DICOM pixel data"}</DialogDescription>
                    </DialogHeader>
                    {preview && <img src={preview.url} alt="DICOM preview" className="w-full max-h-[70vh] object-contain rounded-lg border border-slate-200 bg-black" data-testid="dicom-preview-image" />}
                </DialogContent>
            </Dialog>

            <Dialog open={!!reporting} onOpenChange={() => setReporting(null)}>
                <DialogContent className="max-w-lg" data-testid="radiology-report-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Enter Report — {reporting?.modality}</DialogTitle>
                        <DialogDescription>Record findings to mark this order as reported.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={markReported} className="space-y-4">
                        <div className="space-y-1.5"><Label>Findings / Impression *</Label><Textarea data-testid="radiology-findings-input" rows={5} required value={findings} onChange={(e) => setFindings(e.target.value)} placeholder="e.g. Single live intrauterine pregnancy of 12w2d, CRL 5.4cm, NT 1.2mm..." /></div>
                        <Button data-testid="radiology-report-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Report</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
